/**
 * Take payment (DNKADM-0402 U5) — the small dialog that settles ONE unpaid booking:
 * a card (saved or entered at the desk) or money taken at the counter.
 *
 * **It is the only place the console charges an unpaid booking, and it charges exactly
 * one** (Operator guardrail, 2026-09-22): there is no bulk action, no "charge all
 * unpaid", and check-in never charges. Staff choose the tender and press the button;
 * nothing happens otherwise.
 *
 * The amount is the booking's own `amountDue` — server-owned, displayed here and never
 * sent. The tender controls are the SAME shared ones the create dialog uses
 * ({@link ReservationTenderFields} over `reservationTender.ts`), not a copy.
 *
 * Refusals map through the shared reservations error copy. `409 RESERVATION_NOT_UNPAID`
 * means somebody else already took the money: the booking is re-read so the surface
 * behind this dialog shows the truth, and the dialog says so plainly instead of
 * offering a charge that cannot happen.
 *
 * **An unknown outcome locks the dialog (DNKADM-0475, the DNKADM-0467 rule).** A network
 * error, timeout or 5xx may still have taken the money, so the key and the exact body are
 * kept, the tender is locked, and only "Check again" (the same body under the same key,
 * which dink-api answers without charging twice) can settle it. Closing asks first.
 *
 * **Take payment inside Check in finishes the check-in (DNKADM-0489).** With
 * `checkInAfterPayment`, a payment that lands is followed, in the same click, by the
 * check-in. The order is fixed: the payment first, under its idempotency key; the check-in
 * only once dink-api has answered the payment as taken. A declined or unconfirmed payment
 * never checks anyone in, and the dialog says so. A check-in that fails after the payment
 * landed never re-sends the payment: the dialog keeps the paid booking and offers the
 * check-in alone again (dink-api's check-in is idempotent). A second click while either
 * call is in flight is ignored, so a double click is one charge.
 */
import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import {
  checkInReservation,
  getReservation,
  takeReservationPayment,
  type Reservation,
  type TakeReservationPaymentInput,
} from '@/api/reservations';
import {
  CARD_VERIFICATION_FAILED_MESSAGE,
  CardTokenizationError,
  newIdempotencyKey,
} from '@/api/payments';
import { useOptionalOperator } from '@/auth/authHooks';
import { scrubBrand } from '@/utils/whitelabel';
import { amountDueLabel } from './calendar/derive';
import {
  isAlreadyPaidRefusal,
  toReservationActionErrorMessage,
  toTakePaymentErrorMessage,
} from './reservationsError';
import { useReservationTender, type TenderChoice } from './reservationTender';
import { ReservationTenderFields } from './ReservationTenderFields';
import {
  authRefusalMessage,
  outcomeIsUnknown,
  UNCONFIRMED_PAYMENT_CLOSE_PROMPT,
  unconfirmedMessage,
} from '@/features/members/unconfirmedOutcome';

interface TakePaymentDialogProps {
  open: boolean;
  /** The unpaid booking this payment settles. */
  reservation: Reservation;
  onClose: () => void;
  /**
   * The payment landed: the server's updated (now `paid`) reservation plus the toast.
   * The host replaces its copy in place, exactly as it does for every other write.
   */
  onPaid: (updated: Reservation, message: string) => void;
  /**
   * Somebody else settled it first — the freshly re-read booking, so the surface
   * behind this dialog stops showing Unpaid. No toast: the dialog says it in place.
   */
  onRefreshed: (updated: Reservation) => void;
  /**
   * DNKADM-0489: opened from Check in. A payment that lands is followed by the check-in in
   * the same flow, and `onPaid` receives the re-read, checked-in booking. Off by default:
   * the drawer's own Take payment only settles the money.
   */
  checkInAfterPayment?: boolean;
}

/** DNKADM-0489: said with every payment outcome that leaves the booking not checked in. */
export const NOT_CHECKED_IN_LINE = 'They have not been checked in.';

export function TakePaymentDialog({
  open,
  reservation,
  onClose,
  onPaid,
  onRefreshed,
  checkInAfterPayment = false,
}: TakePaymentDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const titleId = `${ids}-title`;
  const choiceId = `${ids}-tender`;
  const operator = useOptionalOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';

  const [choice, setChoice] = useState<TenderChoice>('card');
  const tender = useReservationTender(choice);
  const [error, setError] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // One key per attempt, kept across a retry of that attempt (a replay returns the
  // same result and never charges twice), dropped when the tender changes.
  const attemptKeyRef = useRef<string | null>(null);
  // DNKADM-0475: the body of a payment the server never confirmed either way, kept so
  // "Check again" re-sends it unchanged (same key, same card nonce or method).
  const [unconfirmed, setUnconfirmed] = useState<TakeReservationPaymentInput | null>(null);
  // DNKADM-0489: the payment landed but the check-in after it did not. The money is done;
  // only the check-in is offered again, never the payment.
  const [paidNotCheckedIn, setPaidNotCheckedIn] = useState<{
    paid: Reservation;
    message: string;
  } | null>(null);
  // Set synchronously on the first click and cleared when the flow settles, so a second
  // click that lands before React re-renders the disabled button is ignored (DNKADM-0489).
  const inFlightRef = useRef(false);
  const locked = submitting || unconfirmed !== null || paidNotCheckedIn !== null;
  useEffect(() => {
    attemptKeyRef.current = null;
  }, [choice, tender.cardOnFileId, tender.paymentMethodId]);

  const uuid = reservation.reservationUuid;
  const amount = amountDueLabel(reservation);
  const methodLabel = scrubBrand(tender.selectedMethod?.label ?? null);

  function close() {
    // A charge in flight is un-dismissable: closing mid-submit would orphan the
    // outcome of a call that has already left the browser.
    if (submitting) return;
    // DNKADM-0475: a payment that may still land is never left silently.
    if (unconfirmed !== null && !window.confirm(UNCONFIRMED_PAYMENT_CLOSE_PROMPT)) return;
    onClose();
  }

  async function submit() {
    if (!uuid || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await submitOnce(uuid);
    } finally {
      inFlightRef.current = false;
    }
  }

  async function submitOnce(uuid: string) {
    setError(null);
    let payment;
    try {
      // DNKADM-0487: a one-time charge of what the booking owes (the same figure the header
      // shows — `amountDue`, else price + tax) for the booking's own contact.
      const due = reservation.amountDue ?? null;
      payment = await tender.buildPayment({
        intent: 'CHARGE',
        amount:
          due !== null && due > 0
            ? due
            : (reservation.priceCharged ?? 0) + (reservation.taxCharged ?? 0),
        currencyCode: reservation.currency ?? null,
        buyer: { name: reservation.contactName, email: reservation.contactEmail },
      });
    } catch (err) {
      setError(
        err instanceof CardTokenizationError
          ? err.verification
            ? CARD_VERIFICATION_FAILED_MESSAGE
            : err.validation
              ? 'Finish entering the card before taking payment.'
              : 'The card could not be read. Check the card details and try again.'
          : 'The payment details are incomplete.',
      );
      return;
    }
    const key = attemptKeyRef.current ?? newIdempotencyKey();
    attemptKeyRef.current = key;
    await send(uuid, { funding: choice, payment, idempotencyKey: key });
  }

  /** DNKADM-0475: settle an unconfirmed payment by re-sending its own body and key. */
  async function checkAgain() {
    if (!uuid || unconfirmed === null || submitting || inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    try {
      await send(uuid, unconfirmed);
    } finally {
      inFlightRef.current = false;
    }
  }

  /** DNKADM-0489: the payment is taken; check them in again without touching the money. */
  async function retryCheckIn() {
    if (!uuid || paidNotCheckedIn === null || inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    try {
      await checkIn(uuid, paidNotCheckedIn.paid, paidNotCheckedIn.message);
    } finally {
      inFlightRef.current = false;
    }
  }

  async function send(uuid: string, input: TakeReservationPaymentInput) {
    setSubmitting(true);
    let paid: Reservation | null = null;
    try {
      const updated = await takeReservationPayment(uuid, input);
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      if (checkInAfterPayment) {
        // The check-in runs below, outside this catch: its failure is not the payment's.
        paid = updated;
      } else {
        onPaid(updated, takenMessage(input.funding, amount, methodLabel));
      }
    } catch (err) {
      if (outcomeIsUnknown(err, unconfirmed !== null)) {
        // The money may have moved: keep the key and the body, lock the tender.
        setUnconfirmed(input);
        // DNKADM-0480: a re-send refused by auth keeps the lock and says why.
        const authLine = authRefusalMessage(err);
        if (authLine !== null) setError(authLine);
        return;
      }
      setUnconfirmed(null);
      if (isAlreadyPaidRefusal(err)) {
        // Nothing was charged. Re-read the booking so the caller's copy is the truth,
        // and stop offering a payment that cannot be taken.
        attemptKeyRef.current = null;
        setSettled(true);
        setError(withNotCheckedIn(toTakePaymentErrorMessage(err)));
        try {
          onRefreshed(await getReservation(uuid));
        } catch {
          // The re-read is a courtesy; the message above is the outcome.
        }
        return;
      }
      setError(withNotCheckedIn(toTakePaymentErrorMessage(err)));
    } finally {
      if (paid === null) setSubmitting(false);
    }
    if (paid !== null) await checkIn(uuid, paid, takenMessage(input.funding, amount, methodLabel));
  }

  /**
   * DNKADM-0489: check in a booking whose payment has just landed, then re-read it so the
   * host shows the booking (and its cancelled no-show fee) as dink-api now has it. `{}` is
   * the plain check-in the settled-booking path already sends: it changes no money, so it
   * needs no manager PIN, and from No-show it cancels the queued fee server-side.
   */
  async function checkIn(uuid: string, paid: Reservation, message: string) {
    setSubmitting(true);
    let checkedIn: Reservation;
    try {
      checkedIn = await checkInReservation(uuid, {});
    } catch (err) {
      setPaidNotCheckedIn({ paid, message });
      setError(`${message} The check-in did not go through: ${toReservationActionErrorMessage(err)}`);
      setSubmitting(false);
      // The surface behind this dialog shows the booking as paid now.
      onRefreshed(paid);
      return;
    }
    let fresh = checkedIn;
    try {
      fresh = await getReservation(uuid);
    } catch {
      // The check-in response is the booking after the check-in; the re-read is a courtesy.
    }
    setPaidNotCheckedIn(null);
    setSubmitting(false);
    onPaid(fresh, `${message} Checked in.`);
  }

  function withNotCheckedIn(line: string): string {
    return checkInAfterPayment ? `${line} ${NOT_CHECKED_IN_LINE}` : line;
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Take payment</Typography>
          <Typography variant="body2">
            {paidNotCheckedIn !== null
              ? 'The payment is taken. Check them in to finish.'
              : amount === null
                ? checkInAfterPayment
                  ? 'Settle this booking and check them in.'
                  : 'Settle this booking.'
                : checkInAfterPayment
                  ? `Unpaid · ${amount} — take it now to mark this booking paid and check them in.`
                  : `Unpaid · ${amount} — take it now to mark this booking paid.`}
          </Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Stack sx={{ gap: 1.5, mt: 0.5 }}>
          <RadioGroup
            aria-labelledby={choiceId}
            value={choice}
            onChange={(_event, next) => {
              tender.reset();
              setChoice(next === 'external' ? 'external' : 'card');
            }}
          >
            <Typography id={choiceId} sx={{ fontSize: 13, fontWeight: 600, color: t.text2 }}>
              How are they paying?
            </Typography>
            <FormControlLabel
              value="card"
              control={<Radio size="small" />}
              disabled={locked || settled}
              label={<Typography sx={{ fontSize: 14 }}>Card</Typography>}
            />
            <FormControlLabel
              value="external"
              control={<Radio size="small" />}
              disabled={locked || settled}
              label={<Typography sx={{ fontSize: 14 }}>Cash or terminal</Typography>}
            />
          </RadioGroup>

          {/* A booking with no linked contact still takes a payment: cash needs no member
              at all, and a card is entered at the desk (the saved-card chooser is the only
              thing a missing contact costs). The server resolves the member from the
              booking either way, so nothing here is a dead end with a permanently disabled
              button and no reason given. */}
          {!settled && paidNotCheckedIn === null && (
            <ReservationTenderFields
              choice={choice}
              contactId={reservation.contactId}
              tenantSlug={tenantSlug}
              locationRef={reservation.locationId ?? ''}
              tender={tender}
              disabled={locked}
            />
          )}

          {unconfirmed !== null && (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void checkAgain()}
                  disabled={submitting}
                >
                  Check again
                </Button>
              }
            >
              {withNotCheckedIn(unconfirmedMessage(unconfirmed.funding))}
            </Alert>
          )}

          {error && (
            <Alert severity={settled ? 'info' : 'error'} role="alert">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          {settled || paidNotCheckedIn !== null ? 'Close' : 'Cancel'}
        </Button>
        {paidNotCheckedIn !== null && (
          <Button
            variant="contained"
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check_circle" />
            }
            onClick={() => void retryCheckIn()}
            disabled={submitting}
          >
            {submitting ? 'Checking in…' : 'Check in'}
          </Button>
        )}
        {!settled && paidNotCheckedIn === null && (
          <Button
            variant="contained"
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check" />
            }
            onClick={() => void submit()}
            disabled={locked || !tender.ready || !uuid}
          >
            {submitting
              ? 'Taking payment…'
              : checkInAfterPayment
                ? 'Take payment and check in'
                : 'Take payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** "Payment taken: $60.00 by card" / "Payment taken: $60.00 recorded as Cash". */
function takenMessage(
  choice: TenderChoice,
  amount: string | null,
  methodLabel: string | null,
): string {
  const how = choice === 'card' ? 'by card' : `recorded as ${methodLabel ?? 'paid at the desk'}`;
  return amount === null ? `Payment taken ${how}.` : `Payment taken: ${amount} ${how}.`;
}
